import React, { useState } from "react";

import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, Typography, CircularProgress, Alert } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../api/client";
import { SchoolContactWithPermissionResponse } from "../api/PartnerPortalGatewayApi";
import { MainLayout } from "../components/layout/MainLayout";
import { SchoolContactList } from "../components/school-contacts";
import { SchoolContactForm } from "../components/school-contacts/SchoolContactForm";
import { CACard } from "../components/ui";
import { useServicePermissions, PartnerPortalAcl } from "../hooks";
import { useAppSelector } from "../store/hooks";

export const SchoolContactsPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<SchoolContactWithPermissionResponse | null>(null);

  const selectedService = useAppSelector((state) => state.service.selectedService);
  const { hasServicePermission, hasGlobalPermission } = useServicePermissions();

  // Check if user has ContactManagement permission
  // Logic matches UserHasServicePermissionAttribute.cs:
  // 1. First check if user has the ACL in global acl claim
  // 2. Then check service-specific permission in partner_service_acls claim
  const hasContactManagementPermission =
    hasGlobalPermission(PartnerPortalAcl.ContactManagement) ||
    (selectedService?.serviceId
      ? hasServicePermission(selectedService.serviceId, PartnerPortalAcl.ContactManagement)
      : false);

  // Query for CRM positions
  const {
    data: positions = [],
    isLoading: positionsLoading,
    error: positionsError,
  } = useQuery({
    queryKey: ["crm-positions"],
    queryFn: () => apiClient.schoolContact.getCrmPositions(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!selectedService, // Only fetch if service is selected
  });

  // Error handling is done via the UI render below, not via notifications during render

  const handleFormSubmit = async () => {
    setShowForm(false);
    setEditingContact(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingContact(null);
  };

  const handleEditContact = (contact: SchoolContactWithPermissionResponse) => {
    setEditingContact(contact);
    setShowForm(true);
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setShowForm(true);
  };

  if (!selectedService) {
    return (
      <MainLayout>
        <Box py={4}>
          <CACard>
            <Box sx={{ padding: 2 }}>
              <Typography variant="h6" color="textSecondary" align="center">
                Please select a service to manage school contacts.
              </Typography>
            </Box>
          </CACard>
        </Box>
      </MainLayout>
    );
  }

  // Continue to show the page but in read-only mode if no permission
  // Users can view contacts but cannot add, edit, or remove them

  // Show loading state while positions are loading
  if (positionsLoading) {
    return (
      <MainLayout>
        <Box py={4}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Box>
      </MainLayout>
    );
  }

  // Show error state if positions failed to load
  if (positionsError) {
    return (
      <MainLayout>
        <Box py={4}>
          <Alert severity="error">Failed to load position data. Please refresh the page to try again.</Alert>
        </Box>
      </MainLayout>
    );
  }

  if (showForm && hasContactManagementPermission) {
    return (
      <MainLayout>
        <Box py={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            {editingContact ? "Edit School Contact" : "Add New School Contact"}
          </Typography>
          <CACard>
            <Box sx={{ padding: 2 }}>
              <SchoolContactForm
                contact={editingContact}
                serviceId={selectedService.serviceId!}
                positions={positions}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            </Box>
          </CACard>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <CACard sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            {!hasContactManagementPermission && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You have view-only access to school contacts. To add, edit, or remove contacts, you need the Contact Management permission.
              </Alert>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h4" component="h1">
                School Contacts
              </Typography>
              {hasContactManagementPermission && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddContact}>
                  Add Contact
                </Button>
              )}
            </Box>
            <SchoolContactList
              serviceId={selectedService.serviceId!}
              positions={positions}
              onEditContact={handleEditContact}
              isReadOnly={!hasContactManagementPermission}
            />
          </Box>
        </CACard>
      </Box>
    </MainLayout>
  );
};
